import React, { useState, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
    useUserTree,
    moveUserNode,
    deleteUserNode,
    liftUserSubtree,
    getAvailableSlots,
} from "../api/users";
import { ReferralTree } from "../components/ReferralTree";
import { UserAutocomplete, User } from "../components/UserAutocomplete";
import { useAuth } from "../hooks/useAuth";
import { useUserFullInfo } from "../api/users";

const UserTreePage: React.FC = () => {
    const { t } = useTranslation("tree");
    const { user } = useAuth();
    const [userId, setUserId] = useState<string>("");
    const [selectedUserId, setSelectedUserId] = useState<number | null>(null); // ID для загрузки дерева
    const [error, setError] = useState<string>("");
    const [operationLoading, setOperationLoading] = useState(false);
    const [parentId, setParentId] = useState<number | null>(null);
    const [childId, setChildId] = useState<number | null>(null);
    const [parentInput, setParentInput] = useState<string>("");
    const [childInput, setChildInput] = useState<string>("");

    // Автоматически загружаем дерево для текущего пользователя при открытии страницы
    useEffect(() => {
        if (user?.id) {
            setUserId(user.id.toString());
            setSelectedUserId(user.id);
        }
    }, [user?.id]);

    const { data: treeData, isLoading, refetch } = useUserTree(selectedUserId);
    const { data: parentInfo } = useUserFullInfo(parentId);
    const { data: childInfo } = useUserFullInfo(childId);

    // Проверяем, является ли пользователь администратором
    const isAdmin = user?.roles?.includes("admin") || false;

    // Функция для перемещения узла
    const handleNodeMove = useCallback(
        async (fromUserId: number, toParentId: number) => {
            if (!selectedUserId) return;

            setOperationLoading(true);
            try {
                await moveUserNode(fromUserId, toParentId);
                // Обновляем дерево после успешного перемещения
                await refetch();
            } catch (err: any) {
                console.error("Error moving node:", err);
                if (err.response?.status === 400) {
                    setError(t("tree.error.invalid_target"));
                } else if (err.response?.status === 403) {
                    setError(t("tree.error.access_denied"));
                } else {
                    setError(err.message || t("tree.error.message"));
                }
            } finally {
                setOperationLoading(false);
            }
        },
        [selectedUserId, refetch, t]
    );

    // Функция для удаления узла
    const handleNodeDelete = useCallback(
        async (nodeUserId: number) => {
            if (!selectedUserId) return;

            setOperationLoading(true);
            try {
                await deleteUserNode(nodeUserId);
                // Обновляем дерево после успешного удаления
                await refetch();
            } catch (err: any) {
                console.error("Error deleting node:", err);
                if (err.response?.status === 400) {
                    setError(t("tree.error.invalid_target"));
                } else if (err.response?.status === 403) {
                    setError(t("tree.error.access_denied"));
                } else {
                    setError(err.message || t("tree.error.message"));
                }
            } finally {
                setOperationLoading(false);
            }
        },
        [selectedUserId, refetch, t]
    );

    // Функция для поднятия поддерева
    const handleNodeLift = useCallback(
        async (nodeUserId: number) => {
            if (!selectedUserId) return;

            setOperationLoading(true);
            try {
                await liftUserSubtree(nodeUserId);
                // Обновляем дерево после успешного поднятия
                await refetch();
            } catch (err: any) {
                console.error("Error lifting subtree:", err);
                if (err.response?.status === 400) {
                    setError(t("tree.error.invalid_target"));
                } else if (err.response?.status === 403) {
                    setError(t("tree.error.access_denied"));
                } else {
                    setError(err.message || t("tree.error.message"));
                }
            } finally {
                setOperationLoading(false);
            }
        },
        [selectedUserId, refetch, t]
    );

    // Функция для получения доступных слотов
    const handleGetAvailableSlots = useCallback(
        async (nodeUserId: number) => {
            try {
                const slots = await getAvailableSlots(nodeUserId);
                alert(
                    `${t("tree.node.actions")} ${nodeUserId}: ${JSON.stringify(
                        slots,
                        null,
                        2
                    )}`
                );
            } catch (err: any) {
                console.error("Error getting available slots:", err);
                setError(t("tree.error.message"));
            }
        },
        [t]
    );

    const handleUserSelect = useCallback((selectedUser: User) => {
        setUserId(selectedUser.id.toString());
        setSelectedUserId(selectedUser.id); // Устанавливаем ID для загрузки дерева
        setError("");
    }, []);

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === "Enter") {
                e.preventDefault();
                const targetUserId = userId.trim();
                const userIdNumber = Number(targetUserId);
                if (targetUserId && !isNaN(userIdNumber)) {
                    setSelectedUserId(userIdNumber); // Устанавливаем ID для загрузки дерева
                    setError("");
                } else {
                    setError(t("tree.error.invalid_id"));
                }
            }
        },
        [userId, t]
    );

    const handleFormSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const targetUserId = userId.trim();
        const userIdNumber = Number(targetUserId);
        if (targetUserId && !isNaN(userIdNumber)) {
            setSelectedUserId(userIdNumber); // Устанавливаем ID для загрузки дерева
            setError("");
        } else {
            setError(t("tree.error.invalid_id"));
        }
    };

    const handleMoveByForm = async () => {
        const resolvedParentId =
            parentId ?? (parentInput.trim() !== "" ? Number(parentInput.trim()) : null);
        const resolvedChildId =
            childId ?? (childInput.trim() !== "" ? Number(childInput.trim()) : null);

        if (!resolvedParentId || !resolvedChildId || isNaN(resolvedParentId) || isNaN(resolvedChildId)) {
            setError(t("error.invalid_id"));
            return;
        }

        setOperationLoading(true);
        try {
            await moveUserNode(resolvedChildId, resolvedParentId);
            await refetch();
            setError("");
        } catch (err: any) {
            if (err.response?.status === 400) {
                setError(t("tree.error.invalid_target"));
            } else if (err.response?.status === 403) {
                setError(t("tree.error.access_denied"));
            } else {
                setError(err.message || t("tree.error.message"));
            }
        } finally {
            setOperationLoading(false);
        }
    };

    return (
        <div className="space-y-6">
            <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                    {t("tree.title")}
                </h2>
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    {isAdmin ? t("description") : t("description")}
                </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-[1fr_2fr] gap-4">
            {/* Форма для ввода user_id - только для администраторов */}
            {isAdmin && (
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
                    <div className="px-4 py-5 sm:p-6">
                        <form onSubmit={handleFormSubmit} className="space-y-4">
                            <div>
                                <label
                                    htmlFor="userId"
                                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                                >
                                    {t("filters.user_id")}
                                </label>
                                <UserAutocomplete
                                    value={userId}
                                    onChange={setUserId}
                                    onUserSelect={handleUserSelect}
                                    onKeyDown={handleKeyDown}
                                    placeholder={t(
                                        "filters.user_id_placeholder"
                                    )}
                                    disabled={isLoading}
                                    className="px-3 py-2 shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                />
                            </div>
                            <div>
                                <button
                                    type="submit"
                                    disabled={isLoading || !userId.trim()}
                                    className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {isLoading ? (
                                        <>
                                            <svg
                                                className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                            >
                                                <circle
                                                    className="opacity-25"
                                                    cx="12"
                                                    cy="12"
                                                    r="10"
                                                    stroke="currentColor"
                                                    strokeWidth="4"
                                                ></circle>
                                                <path
                                                    className="opacity-75"
                                                    fill="currentColor"
                                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                                ></path>
                                            </svg>
                                            {t("loading")}
                                        </>
                                    ) : (
                                        t("search_button")
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {isAdmin && (
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
                    <div className="px-4 py-5 sm:p-6 space-y-4">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                            {t("form.relink_title")}
                        </h3>
                        <div className="grid gap-4 md:grid-cols-2">
                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    {t("form.parent")}
                                </label>
                                <UserAutocomplete
                                    value={parentInput}
                                    onChange={setParentInput}
                                    onUserSelect={(u) => {
                                        setParentId(u.id);
                                        setParentInput(u.id.toString());
                                    }}
                                    placeholder={t("filters.user_id_placeholder")}
                                />
                                {parentInfo && (
                                    <p className="text-xs text-gray-500 break-words">
                                        {parentInfo.name} {parentInfo.surname}
                                        {parentInfo.user_tree?.path
                                            ? ` — path: [${parentInfo.user_tree.path.join(", ")}]`
                                            : ""}
                                    </p>
                                )}
                            </div>
                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    {t("form.child")}
                                </label>
                                <UserAutocomplete
                                    value={childInput}
                                    onChange={setChildInput}
                                    onUserSelect={(u) => {
                                        setChildId(u.id);
                                        setChildInput(u.id.toString());
                                    }}
                                    placeholder={t("filters.user_id_placeholder")}
                                />
                                {childInfo && (
                                    <p className="text-xs text-gray-500 break-words">
                                        {childInfo.name} {childInfo.surname}
                                        {childInfo.user_tree?.path
                                            ? ` — path: [${childInfo.user_tree.path.join(", ")}]`
                                            : ""}
                                    </p>
                                )}
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <button
                                type="button"
                                onClick={handleMoveByForm}
                                disabled={!parentId || !childId || operationLoading}
                                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                            >
                                {operationLoading ? t("common.loading") : t("form.move")}
                            </button>
                            {error && (
                                <span className="text-sm text-red-500">{error}</span>
                            )}
                        </div>
                    </div>
                </div>
                )}
                </div>

            {/* Ошибка */}
            {error && (
                <div className="bg-red-50 border border-red-200 rounded-md p-4">
                    <div className="flex">
                        <div className="flex-shrink-0">
                            <svg
                                className="h-5 w-5 text-red-400"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                            </svg>
                        </div>
                        <div className="ml-3">
                            <h3 className="text-sm font-medium text-red-800">
                                {t("error.title")}
                            </h3>
                            <div className="mt-2 text-sm text-red-700">
                                {error}
                            </div>
                            <div className="mt-4">
                                <button
                                    onClick={() => setError("")}
                                    className="bg-red-100 text-red-800 px-3 py-2 rounded-md text-sm font-medium hover:bg-red-200"
                                >
                                    {t("close")}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Дерево пользователей */}
            {treeData && (
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
                    <div className="px-4 py-4 sm:p-4">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
                                {t("tree.title")}: {treeData.name} (ID:{" "}
                                {treeData.id})
                            </h3>
                            {operationLoading && (
                                <div className="flex items-center text-sm text-blue-600">
                                    <svg
                                        className="animate-spin -ml-1 mr-2 h-4 w-4 text-blue-600"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                    >
                                        <circle
                                            className="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"
                                        ></circle>
                                        <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                        ></path>
                                    </svg>
                                    {t("loading")}
                                </div>
                            )}
                        </div>
                        <div className="max-h-[calc(100vh-350px)] overflow-auto pb-4">
                            <ReferralTree
                                data={treeData}
                                onNodeMove={handleNodeMove}
                                onNodeDelete={handleNodeDelete}
                                onNodeLift={handleNodeLift}
                                onGetAvailableSlots={handleGetAvailableSlots}
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* Инструкции */}
            {!treeData && !isLoading && !error && (
                <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                    <div className="flex">
                        <div className="flex-shrink-0">
                            <svg
                                className="h-5 w-5 text-blue-400"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                            </svg>
                        </div>
                        <div className="ml-3">
                            <h3 className="text-sm font-medium text-blue-800">
                                {t("tree.title")}
                            </h3>
                            <div className="mt-2 text-sm text-blue-700">
                                {isAdmin ? (
                                    <>
                                        <p>{t("description")}</p>
                                        <p className="mt-2">
                                            {t("drag_drop.drag_to_move")}
                                        </p>
                                    </>
                                ) : (
                                    <>
                                        <p>{t("description")}</p>
                                        <p className="mt-2">
                                            {t("drag_drop.drag_to_move")}
                                        </p>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Загрузка */}
            {isLoading && (
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
                    <div className="px-4 py-8 sm:p-8">
                        <div className="flex items-center justify-center">
                            <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                                <svg
                                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-gray-600 dark:text-gray-400"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                >
                                    <circle
                                        className="opacity-25"
                                        cx="12"
                                        cy="12"
                                        r="10"
                                        stroke="currentColor"
                                        strokeWidth="4"
                                    ></circle>
                                    <path
                                        className="opacity-75"
                                        fill="currentColor"
                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                    ></path>
                                </svg>
                                {t("loading")}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserTreePage;
